import React, { useState } from "react";
import adminAxios from "../../config/adminAxios";
import {
  Box,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  Text,
  Button,
  Notification, // ← import Notification
} from "grommet";
import { Edit as EditIcon, Archive, Upload } from "grommet-icons";
import StatusDot from "./StatusDot";
import ArchiveQuestionConfirmationModal from "./ArchiveQuestionConfirmationModal";
import EditQuestionModal from "./EditQuestionModal";

const SkillQuestionsTable = ({
  questions,
  onEdit,
  onArchive,
  onPublish, // optional callback after publish
  showStatusDot,
}) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [questionToArchive, setQuestionToArchive] = useState(null);

  // toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const sortedQuestions = [...questions].sort((a, b) => a.status - b.status);

  // Derive dropdown options — label shows "CODE - Name" but value is just the name
  // so the backend can look up by course name directly
  const courseOptions = Array.from(
    new Map(questions.map((q) => [q.course_name, q])).values(),
  ).map((q) => ({
    label: `${q.course_code} - ${q.course_name}`,
    value: q.course_name,
  }));

  const lpOptions = Array.from(
    new Set(questions.map((q) => q.learning_path_name)),
  ).map((name) => ({ label: name, value: name }));

  const handleEditClick = (question) => {
    setSelectedQuestion(question);
    setEditModalOpen(true);
  };

  const handleArchiveClick = (question) => {
    setQuestionToArchive(question);
    setArchiveModalOpen(true);
  };

  // Publish handler (unchanged)
  const handlePublishClick = async (question) => {
    try {
      await adminAxios.patch("/api/admin/publish-question", {
        ...question,
      });
      setToastMessage("Question published successfully");
      setShowToast(true);
      if (onPublish) onPublish(question.id);
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error("Error publishing question:", error);
    }
  };

  const handleArchiveConfirm = async (question) => {
    try {
      // mirror publish behavior but set status=0
      await adminAxios.patch("/api/admin/archive-question", {
        ...question,
      });
      setToastMessage("Question unpublished successfully");
      setShowToast(true);
      if (onArchive) onArchive(question.id);
      setArchiveModalOpen(false);
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error("Error archiving question:", error);
    }
  };

  return (
    <Box>
      {/* Toast notification */}
      {showToast && (
        <Notification
          toast
          status="normal"
          message={toastMessage}
          onClose={() => {
            setShowToast(false);
            window.location.reload();
          }}
        />
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableCell>
              <Text weight="bold">#</Text>
            </TableCell>
            {showStatusDot && (
              <TableCell>
                <Text weight="bold">Status</Text>
              </TableCell>
            )}
            <TableCell>
              <Text weight="bold">Question</Text>
            </TableCell>
            <TableCell>
              <Text weight="bold">Type</Text>
            </TableCell>
            <TableCell>
              <Text weight="bold">Weight</Text>
            </TableCell>
            <TableCell>
              <Text weight="bold">Course</Text>
            </TableCell>
            <TableCell>
              <Text weight="bold">Learning Path</Text>
            </TableCell>
            <TableCell>
              <Text weight="bold">Actions</Text>
            </TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedQuestions.map((q, i) => (
            <TableRow key={q.id}>
              <TableCell>{i + 1}</TableCell>
              {showStatusDot && (
                <TableCell alignContent="center">
                  <Box
                    align="center"
                    title={q.status === 1 ? "Published" : "Unpublish"}
                  >
                    <StatusDot status={q.status} />
                  </Box>
                </TableCell>
              )}
              <TableCell>{q.question}</TableCell>
              <TableCell>
                {q.weight === 1 ? "Y/N" : q.weight === 2 ? "Range" : "Unknown"}
              </TableCell>
              <TableCell>
                {q.weight === 1
                  ? "Beginner"
                  : q.weight === 2
                    ? "Intermediate/Advanced"
                    : "Unknown"}
              </TableCell>
              <TableCell>{q.course_name}</TableCell>
              <TableCell>{q.learning_path_name}</TableCell>
              <TableCell>
                <Box direction="row" gap="small">
                  <Button
                    icon={<EditIcon color="brand" />}
                    onClick={() => handleEditClick(q)}
                    title="Edit question"
                  />
                  {q.status === 1 ? (
                    <Button
                      icon={<Archive color="status-critical" />}
                      onClick={() => handleArchiveClick(q)}
                      title="Unpublish question"
                    />
                  ) : (
                    <Button
                      icon={<Upload color="brand" />}
                      onClick={() => handlePublishClick(q)}
                      title="Publish question"
                    />
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {editModalOpen && selectedQuestion && (
        <EditQuestionModal
          question={selectedQuestion}
          courses={courseOptions}
          learningPaths={lpOptions}
          onSubmit={async (edited) => {
            const original = selectedQuestion;
            // build delta payload
            const payload = { id: original.id };
            if (edited.question !== original.question) {
              payload.question = edited.question;
            }
            if (edited.weight !== original.weight) {
              payload.weight = edited.weight;
            }
            if (edited.course_name !== original.course_name) {
              payload.course_name = edited.course_name;
            }

            try {
              const res = await adminAxios.patch(
                "/api/admin/update-question",
                payload,
              );
              // show toast and reload
              setToastMessage(
                "Question edited successfully. The question is unpublished — please publish manually.",
              );
              setShowToast(true);
              if (onEdit) onEdit(res.data);
            } catch (err) {
              console.error("Error updating question:", err);
            }
            setEditModalOpen(false);
            setTimeout(() => window.location.reload(), 4000);
          }}
          onCancel={() => setEditModalOpen(false)}
        />
      )}

      {archiveModalOpen && questionToArchive && (
        <ArchiveQuestionConfirmationModal
          question={questionToArchive}
          onConfirm={() => handleArchiveConfirm(questionToArchive)}
          onCancel={() => setArchiveModalOpen(false)}
        />
      )}
    </Box>
  );
};

export default SkillQuestionsTable;
